"use client"

import { useState, useEffect, useRef } from "react"

import Image from "next/image"

import { itemAt } from "@/lib/helpers/array"
import { useScreenVisible } from "@/platform/app-mode/use-screen-visible"

import BillPayIcon from "@public/loading/carousel/BillPay-Icon-Loading.svg"
import CardIcon from "@public/loading/carousel/Card-Icon-Loading.svg"
import CashIcon from "@public/loading/carousel/Cash-Icon-Loading.svg"
import MarketplaceIcon from "@public/loading/carousel/Marketplace-Icon-Loading.svg"
import OnlineIcon from "@public/loading/carousel/Online-Icon-Loading.svg"
import PaymentsIcon from "@public/loading/carousel/Payments-Icon-Loading.svg"
import ShippingIcon from "@public/loading/carousel/Shipping-Icon-Loading.svg"
import TopUpIcon from "@public/loading/carousel/TopUp-Icon-Loading.svg"

interface LoadingTextCarouselProps {
  cycleDuration?: number // Duration for each text/svg combination
  className?: string
}

const TRANSITION_MS = 333

// Matched text-icon configurations
const MATCHED_SLIDES = [
  {
    text: "Paga todos tus servicios a quincenas con Nelo. Sin complicaciones",
    icon: BillPayIcon,
  },
  {
    text: "Recarga saldo en tu celular y paga en cómodos plazos quincenales",
    icon: TopUpIcon,
  },
  {
    text: "Retira efectivo para tu día a día y paga a quincenas. ¡Así de fácil!",
    icon: CashIcon,
  },
  {
    text: "Con Nelo, elige el plan de pagos que mejor se ajuste a tus necesidades",
    icon: PaymentsIcon,
  },
  {
    text: "Conviértete en usuario Nelo VIP Plus y solicita la tarjeta física",
    icon: CardIcon,
  },
  {
    text: "Explora Tienda Nelo y compra miles de productos a quincenas sin intereses",
    icon: MarketplaceIcon,
  },
  {
    text: "Ahórrate el costo de envío en productos seleccionados",
    icon: ShippingIcon,
  },
  {
    text: "Compra a quincenas en tus tiendas favoritas como Mercado Libre, Liverpool, Zara, adidas, Nike y más…",
    icon: OnlineIcon,
  },
]

export const LoadingTextCarousel = ({ cycleDuration = 3000, className = "" }: LoadingTextCarouselProps) => {
  const pairedSlides = MATCHED_SLIDES

  const length = pairedSlides.length

  // Create slides with duplicates for infinite scrolling
  const slides = length > 1 ? [itemAt(pairedSlides, length - 1), ...pairedSlides, itemAt(pairedSlides, 0)] : [...pairedSlides]

  const initialIndex = length > 1 ? 1 : 0

  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [transitionEnabled, setTransitionEnabled] = useState(true)
  const intervalId = useRef<number | null>(null)
  const screenVisible = useScreenVisible()

  const nextSlide = () => {
    setCurrentIndex((prev) => prev + 1)
  }

  useEffect(() => {
    if (length <= 1 || !screenVisible) return

    const startInterval = () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
      intervalId.current = window.setInterval(() => {
        nextSlide()
      }, cycleDuration)
    }

    const stopInterval = () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startInterval()
      } else {
        stopInterval()
      }
    }

    if (document.visibilityState === "visible") {
      startInterval()
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      stopInterval()
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [length, cycleDuration, screenVisible])

  useEffect(() => {
    if (!transitionEnabled) {
      requestAnimationFrame(() => {
        setTransitionEnabled(true)
      })
    }
  }, [transitionEnabled])

  const handleTransitionEnd = () => {
    if (currentIndex === 0) {
      setTransitionEnabled(false)
      setCurrentIndex(length)
    } else if (currentIndex === slides.length - 1) {
      setTransitionEnabled(false)
      setCurrentIndex(1)
    }
  }

  if (length === 0) {
    return null
  }

  const containerStyle = {
    transform: `translateX(-${currentIndex * 100}%)`,
    transition: transitionEnabled ? `transform ${TRANSITION_MS}ms ease-in-out` : "none",
    paddingHorizontal: "10px",
  }

  return (
    <div className={`flex flex-col items-center space-y-6 ${className}`}>
      {/* Combined SVG + Text Carousel */}
      <div className="relative w-full max-w-sm overflow-hidden">
        <div className="flex" style={containerStyle} onTransitionEnd={handleTransitionEnd}>
          {slides.map((slide, index) => (
            <div key={`combo-${index}`} className="flex w-full shrink-0 flex-col items-center justify-center space-y-4 px-4 py-2">
              {/* SVG */}
              <div className="flex h-36 w-36 items-center justify-center">
                <Image src={slide.icon} width={144} height={144} alt="Loading icon" className="object-contain" priority={index < 2} />
              </div>
              {/* Text */}
              <div className="flex min-h-0 items-center justify-center py-4">
                <p className="text-fg max-w-sm text-center text-xl leading-tight font-bold">{slide?.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Variant indicator dots */}
      <div className="flex space-x-2">
        <div className={`h-2 w-2 rounded-full transition-colors ${currentIndex % 2 === 0 ? "bg-nelo-blue" : "bg-gray-300"}`} />
        <div className={`h-2 w-2 rounded-full transition-colors ${currentIndex % 2 === 1 ? "bg-nelo-blue" : "bg-gray-300"}`} />
      </div>
    </div>
  )
}
